import { computed } from 'vue';

import {
  useApiKeysStore,
  useIntegrationProfileStore,
  useOauthApplicationStore,
  useWebhooksStore,
} from '@/stores';
import { API_FILTER_INTEGRATION_TYPE } from '@/types/integration_profiles';

import ApiFiltersTab from './tabs/ApiFiltersTab.vue';
import ApiIntegrationsTab from './tabs/ApiIntegrationsTab.vue';
import ApiKeysTab from './tabs/ApiKeysTab.vue';
import ApiWebhooksTab from './tabs/ApiWebhooksTab.vue';
import type { ApiTabType, ApiTabValueType } from './types';
import useApiKeyCreateGate from './useApiKeyCreateGate';
import useApiTabAccess from './useApiTabAccess';

type ApiTabDetails = Omit<ApiTabType, 'value'>;

export default function useApiTabs() {
  const webhooksStore = useWebhooksStore();
  const oAuthApplicationStore = useOauthApplicationStore();
  const integrationProfileStore = useIntegrationProfileStore();
  const apiKeysStore = useApiKeysStore();
  const { createDisabledReason: createKeyDisabledReason } =
    useApiKeyCreateGate();
  const { accessibleTabs } = useApiTabAccess();

  const tabsByValue = computed<Record<ApiTabValueType, ApiTabDetails>>(() => ({
    webhooks: {
      name: 'Webhooks',
      component: ApiWebhooksTab,
      count: webhooksStore.webhooks.length,
      createCtaText: 'Create webhook',
    },
    integrations: {
      name: 'API Integrations',
      component: ApiIntegrationsTab,
      count: oAuthApplicationStore.oAuthApplications.length,
      createCtaText: 'Create integration',
    },
    filters: {
      name: 'API Filters',
      component: ApiFiltersTab,
      count: integrationProfileStore.getIntegrationProfilesByType(
        API_FILTER_INTEGRATION_TYPE,
      ).length,
      createCtaText: 'Create filter',
    },
    keys: {
      name: 'API Keys',
      component: ApiKeysTab,
      count: apiKeysStore.apiKeys.length,
      createCtaText: 'Create API key',
      createDisabledReason: createKeyDisabledReason.value,
    },
  }));

  const tabs = computed(() =>
    accessibleTabs.value.map((value) => ({
      value,
      ...tabsByValue.value[value],
    })),
  );

  function getTabByValue(value: string | undefined) {
    return tabs.value.find((tab) => tab.value === value);
  }

  return { tabs, getTabByValue };
}
